import styled from '@emotion/styled';
import {
  Check,
  Close,
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  FlashOnOutlined,
  FormatListBulletedOutlined,
  LowPriority,
  QrCodeOutlined,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useChangeBranchCodingProfile from 'api/codingProfile/useChangeBranchCodingProfile/useChangeBranchCodingProfile';
import useMakeDefaultCodingBranch from 'api/codingProfile/useMakeDefaultCodingBranch/useMakeDefaultCodingBranch';
import useProductCoddingDelete from 'api/productDetail/useProductCoddingDelete/useProductCoddingDelete';
import CodingCreateDialog from 'components/shared/BranchSystemCreate/Dialog';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import ChangeCodingProfileModal from 'components/shared/Modals/productsList/ChangeCodingProfileModal/ChangeCodingProfileModal';
import CoddingMappingModal from 'components/shared/Modals/productsList/CoddingMapping/CoddingMapping';
import CreateProductCodding from 'components/shared/Modals/productsList/CreateProductCodding/CreateProductCodding';
import MakeDefaultApproveModal from 'components/shared/Modals/productsList/MakeDefaultApproveModal/MakeDefaultApproveModal';
import ManualProductCodding from 'components/shared/Modals/productsList/ProductCoddingEditModal/ProductCoddingEditModal';
import { CODE_LOGIC } from 'components/shared/productDetail/AccordionCodingList/AccordionCodingList';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { useCallback, useMemo, useState } from 'react';

const coddingPermissions =
  permissions.general.categories.globalSettings.productManagement.productList
    .detail.codding;

const BranchCodingList = ({ codingList, refreshBranchData, branchGuid }) => {
  const { can } = usePermissions();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSetDefaultModalOpen, setIsSetDefaultModalOpen] = useState(false);
  const [changeProfileModalOpen, setChangeProfileModalOpen] = useState(false);
  const [selectedCoding, setSelectedCoding] = useState(null);
  const [selectedSystemEditGuid, setSelectedSystemEditGuid] = useState(null);
  const [editManual, setEditManual] = useState(null);
  const [editMapping, setEditMapping] = useState(null);
  const [isCreateCoddingOpen, setIsCreateCoddingOpen] = useState(false);

  const { deleteLoading, deleteRequest } = useProductCoddingDelete();
  const { loading: isMakeDefaultLoading, makeDefault: makeDefaultRequest } =
    useMakeDefaultCodingBranch();
  const {
    changeBranchCodingProfile,
    loading: isChangeBranchCodingProfileLoading,
  } = useChangeBranchCodingProfile();

  const deleteItem = useCallback(
    row => () => {
      setSelectedCoding(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );

  const onDeleteSuccess = () => {
    setIsDeleteModalOpen(false);
    refreshBranchData();
  };

  const deleteHandle = id => {
    deleteRequest(id, onDeleteSuccess);
  };

  const isMenuOpen = Boolean(anchorEl);

  const handleOpenMenu = (event, coding) => {
    setAnchorEl(event.currentTarget);
    setSelectedCoding(coding);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEdit = useCallback(codingItem => {
    const codingType = String(codingItem?.type ?? '');

    switch (codingType) {
      case '0':
        setSelectedSystemEditGuid(codingItem?.guid);
        return;
      case '1':
        setEditManual({ ...codingItem });
        return;
      case '2':
        setEditMapping({ ...codingItem });
        return;
      default:
        return '';
    }
  }, []);

  function handleEditManual() {
    setEditManual(null);
    refreshBranchData();
  }

  function handleUpdateMapping() {
    setEditMapping(null);
    refreshBranchData();
  }

  function onCoddingCreated() {
    refreshBranchData();
    setIsCreateCoddingOpen(false);
  }

  const setDefaultCodeItem = useCallback(
    row => () => {
      setSelectedCoding(row);
      setIsSetDefaultModalOpen(true);
    },
    [],
  );

  const changeProfileItem = useCallback(
    row => () => {
      setSelectedCoding(row);
      setChangeProfileModalOpen(true);
    },
    [],
  );

  const makeDefaultHandle = () => {
    makeDefaultRequest({
      guid: selectedCoding?.guid,
      onSuccess: () => {
        setIsSetDefaultModalOpen(false);
        refreshBranchData();
      },
    });
  };

  const changeProfileHandle = selectedProfile => {
    changeBranchCodingProfile({
      guid: selectedCoding?.guid,
      newProfile: selectedProfile,
      onSuccess: () => {
        setChangeProfileModalOpen(false);
        refreshBranchData();
      },
    });
  };

  const triggerBatchItem = useCallback(row => () => {}, []);
  const showCodeListItem = useCallback(row => () => {}, []);
  const downloadCodeListItem = useCallback(row => () => {}, []);
  const showSerialListItem = useCallback(row => () => {}, []);
  const downloadSerialListItem = useCallback(row => () => {}, []);

  const menuItems = useMemo(
    () => [
      {
        label: 'حذف',
        permission: coddingPermissions.delete,
        icon: DeleteOutline,
        color: 'error',

        visible: row => !!row && row.type !== '0' && !row.isDefault,

        onClick: row => {
          handleCloseMenu();
          deleteItem(row)();
        },
      },

      {
        label: 'ویرایش',
        permission: coddingPermissions.edit,
        icon: EditOutlined,
        color: 'warning',

        visible: row => !!row && (row.logic === CODE_LOGIC.SKU || !row.logic),

        onClick: row => {
          handleCloseMenu();
          handleEdit(row);
        },
      },

      {
        label: 'تریگر',
        permission: coddingPermissions.batch.trigger,
        icon: FlashOnOutlined,
        color: 'secondary',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          triggerBatchItem(row)();
        },
      },

      {
        label: 'نمایش لیست کد',
        permission: coddingPermissions.batch.view,
        icon: FormatListBulletedOutlined,
        color: 'info',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          showCodeListItem(row)();
        },
      },

      {
        label: 'دانلود لیست کد',
        permission: coddingPermissions.batch.download,
        icon: DownloadOutlined,
        color: 'success',

        visible: row => row?.logic === CODE_LOGIC.BatchNumber,

        onClick: row => {
          handleCloseMenu();
          downloadCodeListItem(row)();
        },
      },

      {
        label: 'نمایش لیست سریال ها',
        permission: coddingPermissions.serial.view,
        icon: FormatListBulletedOutlined,
        color: 'info',

        visible: row => row?.logic === CODE_LOGIC.SerialNumber,

        onClick: row => {
          handleCloseMenu();
          showSerialListItem(row)();
        },
      },

      {
        label: 'دانلود لیست سریال ها',
        permission: coddingPermissions.serial.download,
        icon: DownloadOutlined,
        color: 'success',

        visible: row => row?.logic === CODE_LOGIC.SerialNumber,

        onClick: row => {
          handleCloseMenu();
          downloadSerialListItem(row)();
        },
      },

      {
        label: 'تبدیل به کد پیش‌فرض',
        permission: coddingPermissions.setDefault,
        icon: QrCodeOutlined,
        color: 'primary',

        visible: row =>
          !!row && row.type === '1' && row.isTitleRequired && !row.isDefault,

        onClick: row => {
          handleCloseMenu();
          setDefaultCodeItem(row)();
        },
      },

      {
        label: 'تغییر پروفایل',
        permission: coddingPermissions.profile.edit,
        icon: LowPriority,
        color: 'primary',

        visible: row => row && row.type === '1' && !row.isDefault,

        onClick: row => {
          handleCloseMenu();
          changeProfileItem(row)();
        },
      },
    ],
    [
      deleteItem,
      handleEdit,
      setDefaultCodeItem,
      triggerBatchItem,
      showCodeListItem,
      downloadCodeListItem,
      showSerialListItem,
      downloadSerialListItem,
      changeProfileItem,
    ],
  );

  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter(
        item => item.visible(selectedCoding) && can(item.permission),
      ),
    [menuItems, selectedCoding, can],
  );

  const canCreate = can(coddingPermissions.create);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 4,
          mb: 2,
          paddingX: '16px',
        }}
      >
        <Typography variant="h6" fontSize={18} fontWeight={600}>
          لیست کدینگ
        </Typography>
        {canCreate ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setIsCreateCoddingOpen(true)}
          >
            <AddIcon />
            افزودن کدینگ
          </Button>
        ) : null}
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-root': {
                  borderColor: 'rgba(100, 100, 100, .2)',
                  background: 'rgba(180, 180, 180, 0.05)',
                },
              }}
            >
              <TableCell align="right" width={200}>
                کدینگ
              </TableCell>
              <TableCell align="right">نوع</TableCell>
              <TableCell align="right">عنوان</TableCell>
              <TableCell align="right">عنوان پروفایل</TableCell>
              <TableCell align="right">کد پیش‌فرض</TableCell>
              <TableCell align="left" width={100}>
                عملیات ها
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {codingList?.map((packItem, index) => {
              const rowHasActions = menuItems.some(
                item => item.visible(packItem) && can(item.permission),
              );

              return (
                <TableRow key={packItem.id ?? index}>
                  <TableCell align="right">{packItem.codding}</TableCell>

                  <TableCell align="right">
                    {packItem.type === '0'
                      ? 'سیستمی'
                      : packItem.type === '1'
                      ? 'دستی'
                      : 'مپینگ'}
                  </TableCell>

                  <TableCell align="right">{packItem.display}</TableCell>
                  <TableCell align="right">{packItem.profileName}</TableCell>

                  <TableCell align="right">
                    {packItem.isDefault ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Check />
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Close color="disabled" sx={{ opacity: 0.6 }} />
                      </Box>
                    )}
                  </TableCell>

                  <TableCell align="right" width={180}>
                    {rowHasActions ? (
                      <ActionContainer>
                        <IconButton
                          id={`${packItem.title}-button`}
                          aria-controls={
                            isMenuOpen ? `${packItem.title}-menu` : undefined
                          }
                          aria-haspopup="true"
                          aria-expanded={isMenuOpen ? 'true' : undefined}
                          onClick={e => handleOpenMenu(e, packItem)}
                        >
                          <MenuIcon />
                        </IconButton>
                      </ActionContainer>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          {visibleMenuItems.map(item => {
            const Icon = item.icon;

            return (
              <MenuItem
                key={item.label}
                onClick={() => item.onClick(selectedCoding)}
              >
                <Action>
                  <Typography fontSize={14}>{item.label}</Typography>
                  <Icon fontSize="small" color={item.color} />
                </Action>
              </MenuItem>
            );
          })}
        </Menu>
      </TableContainer>

      {can(coddingPermissions.delete) ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          title={selectedCoding?.display}
          locationId={selectedCoding?.guid}
          deleteLoading={deleteLoading}
          onDelete={() => deleteHandle(selectedCoding?.guid)}
        />
      ) : null}

      {can(coddingPermissions.edit) ? (
        <ManualProductCodding
          open={!!editManual}
          guid={editManual?.guid}
          handleClose={() => setEditManual(null)}
          onUpdate={handleEditManual}
        />
      ) : null}

      {can(coddingPermissions.edit) ? (
        <CoddingMappingModal
          open={!!editMapping}
          guid={editMapping?.guid}
          onClose={() => setEditMapping(null)}
          onUpdate={handleUpdateMapping}
        />
      ) : null}

      {can(coddingPermissions.edit) ? (
        <CodingCreateDialog
          open={!!selectedSystemEditGuid}
          guid={selectedSystemEditGuid}
          onClose={() => setSelectedSystemEditGuid(null)}
          handleConfirm={() => setSelectedSystemEditGuid(null)}
        />
      ) : null}

      {canCreate ? (
        <CreateProductCodding
          open={isCreateCoddingOpen}
          onClose={() => setIsCreateCoddingOpen(false)}
          branchGuid={branchGuid}
          onSuccess={onCoddingCreated}
        />
      ) : null}

      <MakeDefaultApproveModal
        open={isSetDefaultModalOpen}
        handleClose={() => setIsSetDefaultModalOpen(false)}
        isLoading={isMakeDefaultLoading}
        onConfirm={makeDefaultHandle}
      />

      <ChangeCodingProfileModal
        open={changeProfileModalOpen}
        onClose={() => setChangeProfileModalOpen(false)}
        selectedRowInfo={selectedCoding}
        isLoading={isChangeBranchCodingProfileLoading}
        onConfirm={changeProfileHandle}
      />
    </Box>
  );
};

export default BranchCodingList;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const Action = styled(Box)({
  paddingTop: '0.3rem',
  paddingBottom: '0.3rem',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
});
